import { useState } from 'react';
import { Plus, Pencil, Trash } from 'lucide-react';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { WorkshopTemplate } from '../../../interfaces/template.interface';
import { getTypeLabel } from '../../../utils/workshopMigration';
import ConfirmationModal from '../../../components/ConfirmationModal';

interface Props {
  template: WorkshopTemplate;
  onEdit: () => void;
  onAddToPlanning: () => void;
  onDeleted: () => void;
}

const TemplateCard = ({ template, onEdit, onAddToPlanning, onDeleted }: Props) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'workshop_templates', template.id));
      onDeleted();
    } catch (err) {
      console.error('Erreur suppression template :', err);
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  // Get type badge color
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'public':
        return 'bg-blue-100 text-blue-800';
      case 'health':
        return 'bg-green-100 text-green-800';
      case 'teambuilding':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Truncate description for preview
  const truncateHtml = (html: string, maxLength: number = 100) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
        {/* Image */}
        <div className="relative h-48 bg-gray-100">
          {template.imageUrl ? (
            <img
              src={template.imageUrl}
              alt={template.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              Pas d'image
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Type badge */}
          <span
            className={`inline-block px-2 py-1 rounded text-xs font-semibold mb-2 ${getTypeBadgeColor(
              template.type
            )}`}
          >
            {getTypeLabel(template.type)}
          </span>

          {/* Title */}
          <h3 className="text-lg font-bold text-gray-800 mb-2">{template.title}</h3>

          {/* Description preview */}
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {truncateHtml(template.description)}
          </p>

          {/* Info */}
          <div className="text-xs text-gray-500 space-y-1 mb-4">
            <p>Durée : {template.duration} min</p>
            <p>Lieu : {template.location}</p>
            <p>Max participants : {template.maxParticipants}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
            <button
              onClick={onAddToPlanning}
              className="flex items-center gap-1 px-3 py-2 bg-[#623413] text-white rounded text-sm hover:opacity-90 transition-opacity"
              title="Ajouter au planning"
            >
              <Plus size={16} />
              Planning
            </button>

            <div className="flex items-center gap-2">
              <button
                onClick={onEdit}
                className="p-2 text-gray-600 hover:text-[#623413] transition-colors"
                title="Modifier"
              >
                <Pencil size={18} />
              </button>
              <button
                onClick={() => setShowConfirm(true)}
                className="p-2 text-gray-600 hover:text-[#BB0F33] transition-colors"
                title="Supprimer"
                disabled={deleting}
              >
                <Trash size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showConfirm && (
        <ConfirmationModal
          message="Voulez-vous vraiment supprimer ce template ?"
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

export default TemplateCard;
